/**
 * Clean and shape AI reply text. ONE copy, imported by the protagonist-mission
 * edge function AND the website (src/components/protagonist/FormattedReply.tsx),
 * so the server's parse and the client's clean-up cannot drift apart.
 *
 * WHY. Gemini sometimes answers a forced tool call by WRITING the call as text
 * instead of returning tool_calls. protagonist-mission then showed it verbatim:
 * a mission turn on 2026-10-10 ended
 *     ...", choices=["Tell him what you know", ...], mission_phase="opening"))<|END_OF_TOOL_CODE|>
 * parseToolCodeReply recovers the narrative, choices and phase from that text,
 * and cleanReplyText strips any such residue from replies already stored.
 */

export interface ToolCodeReply {
  narrative: string;
  choices: string[];
  missionPhase: string | null;
}

/** Model control tokens, e.g. <|END_OF_TOOL_CODE|>, <|im_end|>. */
const CONTROL_TOKEN = /<\|[A-Za-z_]+\|>/g;
const CODE_FENCE = /```[a-z_]*\n?|```/gi;

function unescapeQuoted(s: string): string {
  return s
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, ' ')
    .replace(/\\(["'\\])/g, '$1');
}

function quotedStrings(listBody: string): string[] {
  const out: string[] = [];
  const re = /"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)'/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(listBody))) out.push(unescapeQuoted(m[1] ?? m[2] ?? '').trim());
  return out.filter(Boolean);
}

/**
 * Recover {narrative, choices, missionPhase} from a tool call written as text.
 * Returns null when the text is not tool code (ordinary prose).
 */
export function parseToolCodeReply(raw: string | null | undefined): ToolCodeReply | null {
  if (!raw) return null;
  const s = raw.replace(CODE_FENCE, '').replace(CONTROL_TOKEN, '').trim();
  const hasChoices = /\bchoices\s*=\s*\[/.test(s);
  const phaseMatch = s.match(/\bmission_phase\s*=\s*["']([a-z_]+)["']/);
  if (!hasChoices && !phaseMatch) return null;

  let narrative = '';
  const named = s.match(/\bnarrative\s*=\s*"((?:[^"\\]|\\.)*)"/) ?? s.match(/\bnarrative\s*=\s*'((?:[^'\\]|\\.)*)'/);
  if (named) {
    narrative = named[1];
  } else {
    // The call's opening was dropped and the text starts mid-argument:
    // everything before `", choices=` / `", mission_phase=` is the narrative.
    const cut = s.search(/["']\s*,\s*(?:choices|mission_phase)\s*=/);
    narrative = cut >= 0 ? s.slice(0, cut) : '';
  }
  const choiceList = s.match(/\bchoices\s*=\s*\[([\s\S]*?)\]/);
  return {
    narrative: unescapeQuoted(narrative).trim(),
    choices: choiceList ? quotedStrings(choiceList[1]).slice(0, 3) : [],
    missionPhase: phaseMatch?.[1] ?? null,
  };
}

/** Reply text fit to show: tool-code residue and control tokens removed. */
export function cleanReplyText(raw: string | null | undefined): string {
  if (!raw) return '';
  const parsed = parseToolCodeReply(raw);
  const text = parsed ? parsed.narrative : raw.replace(CODE_FENCE, '').replace(CONTROL_TOKEN, '');
  return text.replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Split a reply into readable paragraphs.
 * Blank lines are respected. A long single block (models often send one) is
 * broken at sentence boundaries into paragraphs of roughly `target` characters,
 * and a line of dialogue starts its own paragraph, as it would in a novel.
 */
export function toParagraphs(text: string, target = 320): string[] {
  const clean = cleanReplyText(text);
  if (!clean) return [];
  const blocks = clean.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  const out: string[] = [];
  for (const block of blocks) {
    if (block.length <= target * 1.4) { out.push(block.replace(/\n/g, ' ')); continue; }
    const sentences = block.replace(/\n/g, ' ')
      .split(/(?<=[.!?…]["”’)]?)\s+(?=["“‘(]?[A-Z0-9])/);
    let para = '';
    for (const sentence of sentences) {
      const opensDialogue = /^["“]/.test(sentence);
      if (para && (para.length + sentence.length > target || (opensDialogue && para.length > 80))) {
        out.push(para.trim());
        para = '';
      }
      para += (para ? ' ' : '') + sentence;
    }
    if (para.trim()) out.push(para.trim());
  }
  return out;
}
